'use client';

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { InvestigadorPublicProfileResponseSchema } from '@siiv/schemas';
import { apiClient } from '@/lib/apiClient';
import {
	adaptInvestigadorPublicProfile,
	type InvestigatorPublicProfileViewModel,
} from '@/lib/investigadores-public-adapter';
import { useAuthSession } from '@/components/rbac/AuthSessionProvider';
import { suscribirPerfilActualizado } from '@/lib/perfil-actualizado';
import { OwnerViewToggle, type OwnerViewMode } from './OwnerViewToggle';

// El CV lo renderiza el SERVER para todo el mundo (ver `children`); esta copia
// cliente solo la usa el dueño. Se carga en diferido para no meter el árbol del
// CV en el bundle de cada visitante — el dueño ya está esperando el fetch igual.
const CvTab = dynamic(() => import('@/components/investigador/tabs/CvTab').then((m) => m.CvTab));

interface OwnerCvViewProps {
	slug: string;
	/** Owner check: the session's investigador must match this profile. */
	idInvestigador?: number | null;
	/** The PUBLIC CV, already rendered on the server (ISR). */
	children: React.ReactNode;
}

/** Keeps only the rows a visitor would get. */
const onlyPublic = <T extends { esPublico?: boolean }>(items: T[]): T[] =>
	items.filter((item) => item.esPublico !== false);

/** Derives the public lens from the owner's full profile — no second request. */
function toPublicView(full: InvestigatorPublicProfileViewModel): InvestigatorPublicProfileViewModel {
	return {
		...full,
		investigator: {
			...full.investigator,
			personalData: onlyPublic(full.investigator.personalData ?? []),
		},
		cvFormacion: onlyPublic(full.cvFormacion),
		cvExperiencia: onlyPublic(full.cvExperiencia),
		cvCapHumano: onlyPublic(full.cvCapHumano),
		cvPremios: onlyPublic(full.cvPremios),
		cvCargos: onlyPublic(full.cvCargos),
		cvActividades: onlyPublic(full.cvActividades),
		cvEnlaces: onlyPublic(full.cvEnlaces),
	};
}

/**
 * Lets the profile's OWNER switch between what everyone sees and what they see.
 *
 * Why the fetch lives here, on the client:
 *
 * 1. The toggle must flip INSTANTLY. One request to `perfil-completo` returns
 *    every row tagged with its `esPublico`, so the public lens is just a filter
 *    over data already in memory — no refetch, no navigation. Resolving this on
 *    the server would mean a `?vista=` param and a round-trip per flip.
 * 2. The server render stays identical for every visitor, so the public profile
 *    fetch keeps hitting one shared Data Cache entry
 *    (`revalidate: 3600` + tag, see `investigadores-public.ts`) instead of
 *    branching per session.
 *
 * Same shape as `ProfileActions`, which already layers owner-only chrome on top
 * of the shared public page.
 *
 * NOTE: this is NOT what keeps the page cacheable — the route is already dynamic
 * (it awaits `searchParams`, and has no `generateStaticParams`), so there is no
 * Full Route Cache here to protect. The private rows never reach a shared cache
 * regardless: `perfil-completo` is only ever fetched `no-store` / per-session.
 */
export function OwnerCvView({ slug, idInvestigador, children }: OwnerCvViewProps) {
	const { user } = useAuthSession();
	const isOwner = Boolean(
		user?.idInvestigador != null && idInvestigador != null && user.idInvestigador === idInvestigador,
	);
	// Admins get the same public/private preview on ANY profile, not just their own.
	// The backend already authorizes them on `perfil-completo` (assertCanEditProfileBySlug
	// short-circuits for admin), so this only lifts the client-side gate. Same admin
	// check used elsewhere (EditShell, UnidadPendingBadgeGate).
	const isAdmin = Boolean(
		user?.esAdmin || user?.role === 'admin' || user?.role === 'administrador',
	);
	// Whoever may flip lenses. An admin looking at their OWN profile stays an owner
	// (personal wording); on someone else's profile they read as an admin previewer.
	const canPreview = isOwner || isAdmin;

	const [mode, setMode] = useState<OwnerViewMode>('owner');
	const [full, setFull] = useState<InvestigatorPublicProfileViewModel | null>(null);
	// Se incrementa cuando alguien avisa que el perfil cambió; entra en las
	// dependencias del fetch, que es la forma de volver a pedirlo sin duplicar la
	// lógica de carga.
	const [recarga, setRecarga] = useState(0);

	useEffect(
		() => suscribirPerfilActualizado(slug, () => setRecarga((n) => n + 1)),
		[slug],
	);

	useEffect(() => {
		// Nothing to fetch for a plain visitor. No need to clear `full` either: the
		// render below falls back to the public HTML whenever `canPreview` is false.
		if (!canPreview) return;

		let cancelled = false;
		(async () => {
			try {
				const response = await apiClient(`/investigadores/${encodeURIComponent(slug)}/perfil-completo`);
				if (!response.ok) return;
				const parsed = InvestigadorPublicProfileResponseSchema.parse(await response.json());
				if (!cancelled) setFull(adaptInvestigadorPublicProfile(parsed));
			} catch {
				// Best-effort: on failure the server-rendered public CV stays on screen.
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [canPreview, slug, recarga]);

	// Visitors (and the previewer while the full profile loads) get the server HTML as-is.
	if (!canPreview || !full) return <>{children}</>;

	const view = mode === 'public' ? toPublicView(full) : full;

	return (
		<CvTab
			investigator={view.investigator}
			cvPerfil={view.cvPerfil}
			cvFormacion={view.cvFormacion}
			cvExperiencia={view.cvExperiencia}
			cvCapHumano={view.cvCapHumano}
			cvPremios={view.cvPremios}
			cvCargos={view.cvCargos}
			cvActividades={view.cvActividades}
			presentationAction={
				<OwnerViewToggle value={mode} onChange={setMode} audience={isOwner ? 'owner' : 'admin'} />
			}
		/>
	);
}
